// decision-policy-blueprint-line-parser.ts — V2-1 (spec V2 §4.1 "Nguồn thẩm quyền").
//
// Dòng máy đọc `decision_policy: auto|ask` trong mục `## Quyền ủy nhiệm G0`
// của BLUEPRINT (template V2-1p ghi mặc định `decision_policy: ask`). Hàm thuần
// trên TEXT — caller (decision-policy-authority-reader.ts) chỉ đưa vào bytes đã
// khớp `g0.artifact_hash`, không bao giờ file live.
//
// Luật (fail-closed, mọi nhánh lạ đều về `ask`):
//  - không có mục, hoặc mục không có dòng ⇒ `ask`, KHÔNG cảnh báo (vắng = mặc định);
//  - giá trị khác `auto`/`ask`, hoặc nhiều dòng mâu thuẫn ⇒ `ask` + cảnh báo
//    (status hiện, để người biết dòng mình viết không có hiệu lực);
//  - trong mục có chuỗi `decision_policy` mà không đúng dạng dòng máy đọc
//    ⇒ `ask` + cảnh báo (review L3);
//  - dòng nằm NGOÀI mục ⇒ không tính (thẩm quyền chỉ ở mục đã định).
// Tiêu đề so sau NFC (như `## Ngoài phạm vi`, R-20); giá trị không phân biệt hoa thường.
import { BLUEPRINT_SECTION_END } from "./blueprint-gate-hash.js";

export const DECISION_POLICY_SECTION_HEADING = "## Quyền ủy nhiệm G0";

export type DecisionPolicyValue = "auto" | "ask";

export interface ParsedDecisionPolicy {
  policy: DecisionPolicyValue;
  /** Chỉ có khi dòng có mặt nhưng không đọc được — vắng dòng không cảnh báo. */
  warning?: string;
}

const POLICY_LINE = /^[-*+]?\s*`?decision_policy\s*:\s*([^`]*?)`?\s*$/i;

export function parseDecisionPolicyFromBlueprint(text: string): ParsedDecisionPolicy {
  const lines = text.split(/\r?\n/);
  const heading = DECISION_POLICY_SECTION_HEADING.normalize("NFC");
  const start = lines.findIndex((line) => line.trim().normalize("NFC") === heading);
  if (start === -1) return { policy: "ask" };

  const values: string[] = [];
  let nearMiss: string | undefined;
  for (const line of lines.slice(start + 1)) {
    const trimmed = line.trim();
    if (BLUEPRINT_SECTION_END.test(trimmed)) break;
    const match = POLICY_LINE.exec(trimmed);
    if (match !== null) values.push((match[1] ?? "").trim().toLowerCase());
    // Review L3: viết gần đúng (`**decision_policy**`, `> …`, `decision-policy`) ⇒ cảnh báo, không im lặng.
    else if (/decision[_-]policy/i.test(trimmed)) nearMiss ??= trimmed;
  }
  if (nearMiss !== undefined) {
    return {
      policy: "ask",
      warning: `dòng "${nearMiss.slice(0, 60)}" không đúng dạng \`decision_policy: auto|ask\` — dùng ask.`,
    };
  }
  if (values.length === 0) return { policy: "ask" };

  const distinct = [...new Set(values)];
  if (distinct.length > 1) {
    return {
      policy: "ask",
      warning: `BLUEPRINT có ${values.length} dòng decision_policy mâu thuẫn (${distinct.join(", ")}) — dùng ask.`,
    };
  }
  const value = distinct[0];
  if (value === "auto" || value === "ask") return { policy: value };
  return {
    policy: "ask",
    warning: `decision_policy "${(value ?? "").slice(0, 40)}" không hợp lệ (chỉ auto|ask) — dùng ask.`,
  };
}
